<script setup lang="ts">
import { TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  STATUS_LABELS,
  STATUS_TAG,
  TASK_STATUS_LABELS,
  cancelWave,
  completeWave,
  donePickTask,
  getWave,
  type Detail,
  type PickTaskStatus,
  type TaskRow,
  type WaveStatus,
} from '../api/waves'

const route = useRoute()
const router = useRouter()

const detail = ref<Detail | null>(null)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')
const busy = ref(false)

const waveId = computed(() => Number(route.params.id))
const wave = computed(() => detail.value?.wave ?? null)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    detail.value = await getWave(waveId.value)
    notice.value = noticeText
  } catch (e) {
    error.value = e
    detail.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function back(): void {
  void router.push({ name: 'waves' })
}

/** 按来源库位分组：拣货员照着一个库位一次拣完，再换下一个 */
const groups = computed(() => {
  const map = new Map<string, TaskRow[]>()
  for (const task of detail.value?.tasks ?? []) {
    const key = task.from_location_code || `#${task.from_location_id}`
    const list = map.get(key)
    if (list) list.push(task)
    else map.set(key, [task])
  }
  return [...map.entries()].map(([code, tasks]) => ({ code, tasks }))
})

const pendingCount = computed(
  () => (detail.value?.tasks ?? []).filter((task) => task.status === 'pending').length,
)

async function pick(task: TaskRow): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await donePickTask(task.id)
    await load(`任务 ${task.task_no} 已完成`)
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

async function cancel(): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await cancelWave(waveId.value)
    await load('波次已取消，库存锁定已释放，出库单退回已释放')
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

async function complete(): Promise<void> {
  notice.value = ''
  error.value = null
  busy.value = true
  try {
    await completeWave(waveId.value)
    await load('复核发货完成，库存已扣减')
  } catch (e) {
    error.value = e
  } finally {
    busy.value = false
  }
}

const canPick = computed(
  () =>
    !!wave.value &&
    (wave.value.status === 'open' || wave.value.status === 'picking'),
)
const canCancel = computed(() => canPick.value)
const canComplete = computed(() => wave.value?.status === 'picked')
</script>

<template>
  <div class="yc-waved" v-loading="loading">
    <header class="yc-waved__bar">
      <el-button @click="back">返回列表</el-button>
      <template v-if="wave">
        <span class="yc-waved__docno">{{ wave.doc_no }}</span>
        <el-tag :type="STATUS_TAG[wave.status as WaveStatus]" size="small">
          {{ STATUS_LABELS[wave.status as WaveStatus] ?? wave.status }}
        </el-tag>
        <div class="yc-waved__spacer" />
        <el-button
          v-perm="'wms:wave:ship'"
          v-if="canComplete"
          :loading="busy"
          type="primary"
          @click="complete"
        >
          复核发货
        </el-button>
        <el-popconfirm
          v-if="canCancel"
          title="确认取消该波次？库存锁定会释放，出库单退回已释放。"
          width="300"
          @confirm="cancel"
        >
          <template #reference>
            <el-button v-perm="'wms:wave:cancel'" type="danger">取消波次</el-button>
          </template>
        </el-popconfirm>
      </template>
    </header>

    <div v-if="error" class="yc-waved__error">
      <TraceIdAlert :error="error" />
    </div>
    <div v-if="notice" class="yc-waved__notice">{{ notice }}</div>

    <template v-if="detail">
      <el-descriptions class="yc-waved__meta" :column="4" border>
        <el-descriptions-item label="仓库">
          {{ detail.warehouse_name || `#${wave!.warehouse_id}` }}
        </el-descriptions-item>
        <el-descriptions-item label="出库单数">{{ detail.orders.length }}</el-descriptions-item>
        <el-descriptions-item label="任务数">{{ detail.tasks.length }}</el-descriptions-item>
        <el-descriptions-item label="待拣任务">{{ pendingCount }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ wave!.created_by }}</el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="3">{{ formatDateTime(wave!.created_at) }}</el-descriptions-item>
      </el-descriptions>

      <el-card class="yc-waved__card">
        <template #header>
          <span>包含的出库单</span>
        </template>
        <el-table :data="detail.orders" row-key="id">
          <el-table-column prop="doc_no" label="出库单号" min-width="180" />
          <el-table-column prop="status" label="状态" width="120">
            <template #default="{ row }">{{ row.status }}</template>
          </el-table-column>
          <el-table-column label="详情" width="110">
            <template #default="{ row }">
              <el-button link type="primary" @click="router.push({ name: 'outbound-detail', params: { id: row.id } })">
                查看
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card v-for="group in groups" :key="group.code" class="yc-waved__card">
        <template #header>
          <span>库位 {{ group.code }}</span>
          <span class="yc-waved__hint">
            {{ group.tasks.filter((task) => task.status === 'done').length }} / {{ group.tasks.length }} 已完成
          </span>
        </template>
        <el-table :data="group.tasks" row-key="id">
          <el-table-column prop="task_no" label="任务号" width="180" />
          <el-table-column prop="sku_code" label="商品编码" width="140" />
          <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
          <el-table-column prop="batch_no" label="批次" width="120">
            <template #default="{ row }">{{ row.batch_no || '—' }}</template>
          </el-table-column>
          <el-table-column prop="qty" label="数量" width="90">
            <template #default="{ row }">{{ row.qty }} {{ row.unit }}</template>
          </el-table-column>
          <el-table-column prop="order_doc_no" label="出库单号" min-width="170" />
          <el-table-column label="状态" width="96">
            <template #default="{ row }">
              <el-tag
                :type="row.status === 'done' ? 'success' : row.status === 'cancelled' ? 'danger' : 'warning'"
                size="small"
              >
                {{ TASK_STATUS_LABELS[row.status as PickTaskStatus] ?? row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="110" fixed="right">
            <template #default="{ row }">
              <el-button
                v-if="row.status === 'pending' && canPick"
                v-perm="'wms:wave:pick'"
                :loading="busy"
                link
                type="primary"
                @click="pick(row)"
              >
                完成
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<style scoped lang="scss">
.yc-waved {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-3);
  }

  &__docno {
    font-weight: 600;
  }

  &__spacer {
    flex: 1;
  }

  &__meta {
    margin-bottom: var(--yc-space-4);
  }

  &__card {
    margin-bottom: var(--yc-space-4);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__notice {
    margin-bottom: var(--yc-space-3);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    margin-left: var(--yc-space-2);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
  }
}
</style>
